/* --------------------------------- Zeile ----------------------------------- */
// Schritt 3 (Konzept § 4.3): groesster Einzelblock der Gruppe "bereits datei-lokal isolierte
// Komponenten" (~315 Zeilen). Reiner Ortswechsel wie Schritt 2 -- ItemRow nimmt ausschliesslich
// Props/Callbacks entgegen, keine Closures ueber BuilderPage-State. Die H-12-Waechterzeilen
// (`if (!editable) return; // H-12`) leben NICHT hier, sondern in den Callbacks, die builder.tsx
// als Props hereinreicht (patchItem/removeItem/moveItemInSection/... in builder.tsx) -- ItemRow
// selbst enthaelt keine einzige solche Zeile, `cycleMode`/`cycleVat` unten rufen nur `onPatch`.
import { useEffect, useMemo, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  VAT_RATES,
  kindToSection,
  lineTotalCaption,
  leseZeile,
  milliToQty,
  qtyToMilli,
  useBufferedField,
  vatLabel,
} from "../lib/builder-helpers";
import type { CatalogItem, OfferItem, OfferPriceMode, OfferSection } from "./types";
import { centsToEuros, money, parseGermanEurosToCents } from "./format";
import type { PauschalTeil } from "@maestro/pricing";
import { Button, Checkbox, Icon, Input, Select, StatusPill } from "./components";
import { AllergenBadges, activeDietary } from "./lmiv";
import { allergeneFehlen as lmivLuecke, kartenTreffer, useAbgelehnteVorschlaege, vorschlagAblehnen } from "../lib/allergen-vorschlag";
import { RASTER_43 } from "./builder-raster";
import { klingtNachPortionsgroesse } from "@maestro/portionsgroesse-heuristik";

/**
 * Preismodus je Zeile -- steuert, was "Menge" bedeutet (siehe lineGross in packages/pricing):
 *  - per_person: Menge = Portionen PRO GAST, wird automatisch mit der Gästezahl multipliziert.
 *    Normalfall fuer individuelle Gänge (Vorspeise, Hauptgang je Gast) -> Menge bleibt meist 1.
 *  - per_unit: Menge = Anzahl STUECK INSGESAMT (z. B. Platten für mehrere Gäste), OHNE
 *    Gästezahl-Multiplikation. Bei einer Platte für 6 Gäste und 99 Gästen: Menge = 17 (99/6
 *    aufgerundet), Preis = Preis PRO PLATTE (nicht pro Gast).
 *  - flat: Menge wirkungslos, Einzelpreis = Zeilensumme (Pauschale).
 */
/** Punkt 32, § 11: bei „pro Person" ist die Menge die Gästezahl — eine eigene Höchstmenge wäre wirkungslos. */
const MENGE_AUS_GAESTEZAHL = "Die Menge ergibt sich aus der Gästezahl.";

/** Der eine Hinweis-Stil (Mockup 13.10.2026): Symbol + Text in Grau, kein Hintergrund. */
const HINWEIS = "inline-flex items-center gap-1 text-[11.5px] font-medium text-[#6F675A] dark:text-secondary-fixed-dim";

const PRICE_MODES: { mode: OfferPriceMode; label: string; hint: string }[] = [
  { mode: "per_person", label: "/Pers.", hint: "Menge = Portionen pro Gast (× automatisch mit Gästezahl multipliziert)" },
  { mode: "per_unit", label: "/Stk.", hint: "Menge = Stück insgesamt (z. B. Platten) — Preis pro Stück, NICHT pro Gast" },
  { mode: "flat", label: "pauschal", hint: "Pauschalpreis — Menge ohne Wirkung" },
];

/** Migration 725: „für [3] von 4 Gästen“ — leer, ungültig oder alle Gäste heißt null (gilt für alle). */
function TeilgruppeEingabe({ item, guests, onPatch }: { item: OfferItem; guests: number; onPatch: (p: Partial<OfferItem>) => void }) {
  return (
    <Input
      type="number"
      min={1}
      max={guests}
      data-no-dnd="true"
      data-testid="paket-teilgruppe"
      aria-label="Für wie viele Gäste gilt das Paket?"
      className="!w-14 !py-0.5 text-right text-body-sm"
      placeholder={String(guests)}
      value={item.guestCount ?? ""}
      onChange={(e) => {
        const n = Number(e.target.value);
        onPatch({ guestCount: e.target.value === "" || !Number.isInteger(n) || n < 1 || n >= guests ? null : n });
      }}
    />
  );
}

/**
 * Menge ist bei „pro Gast“ die Portion JE Gast. Wer 50/20/5 Gäste auf drei Menüs aufteilen will und
 * das in „Menge“ tippt, rechnet 50 Portionen × alle Gäste (V-1169: 442.500 € statt 5.900 €). Der
 * Hinweis zeigt den richtigen Weg; der Knopf übernimmt die Zahl als Gästeaufteilung. Gerechnet wird
 * weiter ausschließlich in packages/pricing.
 */
function MengeAlsGaesteHinweis({ item, guests, onPatch }: { item: OfferItem; guests: number; onPatch: (p: Partial<OfferItem>) => void }) {
  if (item.priceMode !== "per_person" || guests <= 1 || item.qtyMilli <= 1000) return null;
  const ganzzahlig = item.qtyMilli % 1000 === 0;
  const n = item.qtyMilli / 1000;
  return (
    <div className="mt-2 rounded-md bg-[#FBF1DC] px-3 py-2 text-[12.5px] text-[#6B4E12] dark:bg-white/[0.06] dark:text-[#e0c98f]" data-no-dnd="true" data-testid="menge-gaeste-hinweis">
      <p>
        Menge = Portionen pro Gast (× {guests} Gäste). Gäste aufteilen? Dann Menge 1 und „für … von {guests} Gästen“ eintragen.
      </p>
      {ganzzahlig && (
        <Button
          type="button"
          variant="ghost"
          data-no-dnd="true"
          data-testid="menge-als-gaesteaufteilung"
          className="mt-1 !px-2 !py-1 text-beleg-mikro text-primary"
          onClick={() => onPatch(n < guests ? { qtyMilli: 1000, guestCount: n } : { qtyMilli: 1000, guestCount: null })}
        >
          Als Gästeaufteilung übernehmen
        </Button>
      )}
    </div>
  );
}

export function ItemRow({
  item,
  section,
  catalogItems,
  guests,
  lmiv,
  bearbeiten,
  einzeln,
  kollabiert,
  pauschaleBlock = false,
  onPatch,
  onRemove,
  onOeffnen,
  onFertig,
  onAbbrechen,
  umsortierbar,
  onGruppeVerlassen,
  verbindbar,
  onVerbinden,
  inWahlgruppe = false,
  imPaket = false,
  nachKarte = false,
  paketKopf,
  onNeuerGangAbHier,
}: {
  item: OfferItem;
  /** Sektion DIESER Zeile (vom umgebenden Scope in `PositionsListe.tsx` als `key` bekannt) —
   *  ItemRow kennt seine eigene Sektion sonst nicht, braucht sie aber, um `catalogItems` unten
   *  auf die passende Katalog-Art einzugrenzen (`kindToSection`, dasselbe Muster wie
   *  `PositionHinzufuegenDrawer.tsx`s `filteredCatalog`). */
  section: OfferSection;
  /** Katalog-Vorschläge fuers Bezeichnungsfeld (Betreiber tippt -> gefilterte Liste darunter,
   *  Substring auf Name/Beschreibung, s. `filteredCatalog` in `PositionHinzufuegenDrawer.tsx`).
   *  Ein Klick uebernimmt den Namen UND verknuepft die Katalog-Position (`catalogItemId`), damit
   *  die Allergene aus der Karte kommen (Antoine 12.10.2026). Der Preis bleibt unveraendert —
   *  Betraege werden nie ueberschrieben. */
  catalogItems: CatalogItem[];
  guests: number;
  /** Allergene/Diät der verknüpften Katalog-Position (undefined = Freitext ohne Datenbasis).
   *  allergensCheckedAt (H-02-Rest, Migration 336): nur bei allergens=[] relevant — fehlt/null
   *  -> ungeprüfter Alt-Bestand statt „geprüft: keine". */
  lmiv?: { allergens: string[] | null; allergensCheckedAt?: string | null; dietary: Record<string, boolean> };
  /**
   * Entwurf 43: Diese Zeile steht in der eingerueckten Karte einer Wahlgruppe. Dann traegt die
   * erste Spalte einen Radio-Ring statt der Griff-Punkte — in einer Wahlgruppe geht es um eine
   * WAHL zwischen Alternativen, nicht um eine Reihenfolge.
   *
   * Es ist reine Anzeige, wie die Punkte auch: die ganze Zeile bleibt der Griff, umsortieren
   * geht unveraendert. Und es ist KEIN Bedienelement — der Gast waehlt spaeter im Dokument,
   * nicht der Betreiber hier. Deshalb kein `input`, kein Zustand, kein Klickziel.
   */
  inWahlgruppe?: boolean;
  /** B-4: false = ruhige Lesezeile (Entwurf 31a), true = das unveraenderte Bearbeitungsformular. */
  bearbeiten: boolean;
  /**
   * B-4: true = diese Zeile wurde EINZELN geoeffnet (Entwurf 31b) und traegt darum ihre eigenen
   * Knoepfe „Fertig"/„Abbrechen". Bei „Alle bearbeiten" gehoert der Abschluss in den
   * Abschnittskopf — zwanzig „Fertig"-Knoepfe untereinander waeren keine Hilfe.
   */
  einzeln: boolean;
  /**
   * R34-2 (Konzept § 2, „Positionspreise im Pauschalmodus"), eingegrenzt auf Menü (Antoine
   * 28.08.2026): true, wenn die Preis-Engine diese Zeile im Gesamtpreis-Modus kollabiert
   * (`packages/pricing/src/index.ts::istSektionsKern` — `sectionPricing[section].mode==='total'
   * && item.isOptional!==true`, seit dem Bughunt 27.08.2026 fuer JEDE gesamtpreisfaehige Sektion,
   * nicht nur Menü) UND es sich um die Sektion „menu" handelt (siehe PositionsListe.tsx). Die
   * Rechnung selbst bleibt fuer alle Sektionen unveraendert — nur diese Anzeige (gedaempft +
   * durchgestrichen + „zählt nicht") ist bewusst auf Menü beschraenkt, weil sie bei Getraenken
   * wie ein Fehler statt wie eine Erklaerung wirkte.
   */
  kollabiert: boolean;
  /**
   * Migration 722 (Antoine 14.10.2026): die Sektion hat eine Pauschale („je Gast“ oder „gesamt“).
   * Dann ersetzt eine Auswahl mit drei Stufen den Knopf „zubuchbar“: inklusive · zzgl. · zubuchbar.
   */
  pauschaleBlock?: boolean;
  onPatch: (p: Partial<OfferItem>) => void;
  onRemove: () => void;
  /** B-4: Zeile einzeln zum Bearbeiten aufklappen. `undefined` = nicht bearbeitbar (H-12). */
  onOeffnen?: () => void;
  /** B-4: Bearbeitung uebernehmen und zuklappen (das Gespeicherte bleibt). */
  onFertig?: () => void;
  /** B-4: auf den Stand beim Oeffnen zuruecksetzen und zuklappen. */
  onAbbrechen?: () => void;
  /** Umsortieren: `false`/`undefined`, solange die Variante nicht editierbar ist (H-12) —
   *  dann ist die Zeile kein Drag-Ziel, analog zu `onOeffnen`. */
  umsortierbar?: boolean;
  /**
   * Migration 384: "Aus Wahlgruppe lösen" — setzt `choiceGroupId` auf null (`onPatch`
   * darunter, kein Sonderweg). Nur gerendert, wenn `item.choiceGroupId != null` UND die Zeile
   * bearbeitbar ist (`onOeffnen` gesetzt, H-12-Gate wie Bearbeiten/Loeschen).
   */
  onGruppeVerlassen?: () => void;
  /**
   * sql/753: die anderen zubuchbaren Positionen der Variante, mit denen sich diese per „und“ zu EINEM
   * Schalter auf der Gastseite verbinden lässt (z. B. Mindestumsatz und Raummiete). `gruppe` = deren
   * bestehende Verbindung. `onVerbinden(null)` löst die Verbindung.
   */
  verbindbar?: { id: string; name: string; gruppe: string | null }[];
  onVerbinden?: (mitId: string | null) => void;
  /**
   * Builder-Neugestaltung (Mockup v3, 28.09.2026): die Zeile ist ein Bestandteil eines Pakets,
   * dessen bepreiste Kopfzeile in derselben Variante steht (`ungedeckteNullpreisPositionen` aus
   * `@maestro/pricing` entscheidet das — dieselbe Regel wie der Versand-Riegel). Dann steht statt
   * „20× à 0,00 € 0,00 €" ein ruhiges „im Paket": der Preis steht einmal am Paket-Kopf.
   */
  imPaket?: boolean;
  /** À-la-carte-Variante: kein Betrag je Zeile, stattdessen „nach Karte“ (nur Anzeige). */
  nachKarte?: boolean;
  /**
   * Mockup v3: diese Zeile IST die bepreiste Paket-Kopfzeile (`packageId` gesetzt, ohne
   * Katalogbezug). Sie wird als Kopf gezeigt — Name, Beschreibung, Preis pro Gast × Gäste — statt
   * als Tabellenzeile. `enthalten` ist der Satz „Alle 4 Gänge sind im Paketpreis enthalten", nur
   * wenn er stimmt (sonst `null`).
   */
  paketKopf?: { enthalten: string | null; aufteilung?: { teile: PauschalTeil[]; ausPaket: boolean } };
  /** Mockup v3: „Neuer Gang ab hier" — diese und die folgenden Positionen des Gangs bekommen eine
   *  eigene Überschrift. Nur im Menü und nicht an der ersten Position eines Gangs gesetzt. */
  onNeuerGangAbHier?: () => void;
}) {
  /* Umsortieren (dnd-kit): `useSortable` bindet DIESE Zeile an ihre `id` innerhalb der
   * umschliessenden `SortableContext` (siehe Render der Sektionen). `setNodeRef` + `style`
   * sorgen fuer die Verschiebe-Animation waehrend des Ziehens.
   *
   * UX-Wunsch (Antoine, 23.08.2026, aufgaben.md): die frueheren Umsortier-Icons (kleiner
   * Griff + separate Auf/Ab-Pfeile, je 18px) machten die Ansicht "sehr unruhig" UND man musste
   * den Griff exakt treffen, um ueberhaupt ziehen zu koennen. Jetzt traegt die GANZE Zeile
   * `attributes`/`listeners` statt eines einzelnen Icons — kein separates Griff-Element mehr.
   * Die Auf/Ab-Pfeile (vormals ein bespoke Tastatur-/Touch-Fallback, `moveItemInSection` in
   * builder.tsx) sind damit nicht ersatzlos gestrichen, sondern durch dnd-kits EIGENEN
   * `KeyboardSensor` abgeloest: sobald die Zeile (statt eines 18px-Icons) die sortable-
   * Attribute traegt, ist sie selbst per Tab fokussierbar und per Leertaste/Pfeiltasten
   * bedienbar (dnd-kit-Standardmuster, groesseres/robusteres Ziel als vorher).
   *
   * Klicks auf interaktive Unterelemente (Bearbeiten-/Loeschen-Icon, Eingabefelder, Knoepfe)
   * duerfen dabei NICHT als Drag-Start missverstanden werden: jedes solche Element traegt
   * `data-no-dnd`, die beiden Handler unten reichen den dnd-kit-Aufruf nur weiter, wenn das
   * tatsaechliche Ziel NICHT innerhalb eines `data-no-dnd`-Elements liegt. Der bestehende
   * Maus-/Touch-`activationConstraint` (4px bzw. Halten, ui/PositionsListe.tsx) bleibt zusaetzlich
   * bestehen — beide zusammen sind das im Auftrag selbst genannte gaengige dnd-kit-Muster. */
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const dragStyle = { transform: CSS.Transform.toString(transform), transition };
  const dndHandlers = umsortierbar
    ? {
        ...attributes,
        onMouseDown: (e: React.MouseEvent) => {
          if ((e.target as HTMLElement).closest("[data-no-dnd]")) return;
          listeners?.onMouseDown?.(e);
        },
        // Antoine 15./16.10.2026: am Handy verschob Wischen zum Scrollen die Position. Mit dem Finger
        // zieht darum nur der Griff (Punkte links), und erst nach kurzem Halten (TouchSensor,
        // `DND_TOUCH_HALTEN` in PositionsListe.tsx); mit der Maus bleibt die ganze Zeile der Griff.
        onTouchStart: (e: React.TouchEvent) => {
          if (!(e.target as HTMLElement).closest("[data-drag-griff]")) return;
          listeners?.onTouchStart?.(e);
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          if ((e.target as HTMLElement).closest("[data-no-dnd]")) return;
          listeners?.onKeyDown?.(e);
        },
      }
    : {};
  // Kein `touch-none`, auch nicht am Griff: der Finger muss überall scrollen können (Ziehen = Halten).
  const rowDragClass = umsortierbar ? "cursor-grab active:cursor-grabbing" : "";
  /**
   * Mockup v3: Auf Geräten OHNE Zeiger zum Darüberfahren (Handy, Tablet) sind die Werkzeuge
   * ausgeblendet — dort öffnet ein Tippen auf die Zeile das Bearbeiten (mit Entfernen, „Aus
   * Wahlgruppe lösen" und „Neuer Gang ab hier" im Formular). Ein Tippen auf ein Bedienelement
   * in der Zeile bleibt, was es ist. Halten und Ziehen verschiebt wie bisher (dnd-kit).
   */
  const antippen = (e: React.MouseEvent) => {
    if (!onOeffnen || bearbeiten) return;
    if ((e.target as HTMLElement).closest("[data-no-dnd], [data-drag-griff], a, button, input")) return;
    if (typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches) onOeffnen();
  };
  /**
   * Mockup v3: Werkzeuge (Stift, Lösen, Entfernen …) erscheinen beim Darüberfahren UND bei
   * Tastaturfokus in der Zeile — sie bleiben im Tab-Fluss. Ohne Zeiger (Touch) übernimmt das
   * Tippen auf die Zeile (siehe `antippen`).
   */
  const werkzeugKlasse =
    "transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/zeile:opacity-100 [@media(hover:hover)]:group-focus-within/zeile:opacity-100 [@media(hover:none)]:hidden";
  const cycleMode = () => {
    const idx = PRICE_MODES.findIndex((m) => m.mode === item.priceMode);
    const mode = PRICE_MODES[(idx + 1) % PRICE_MODES.length]!.mode;
    // Spritz-Menge (13.10.2026): Menge mit umrechnen, sonst werden aus 65 Stück 65 je Gast (× 65).
    // „je Gast" → 1 je Gast, „Stück" → Gästezahl, „pauschal" → Menge unverändert (wirkungslos).
    if (mode === "per_person") onPatch({ priceMode: mode, qtyMilli: 1000 });
    else if (mode === "per_unit" && guests > 0) onPatch({ priceMode: mode, qtyMilli: guests * 1000 });
    else onPatch({ priceMode: mode });
  };
  const cycleVat = () => {
    const idx = VAT_RATES.findIndex((r) => r.bp === item.vatRateBp);
    onPatch({ vatRateBp: VAT_RATES[(idx + 1) % VAT_RATES.length]!.bp });
  };
  const override = item.catalogPriceCents != null && item.catalogPriceCents !== item.unitPriceCents;
  const modeMeta = PRICE_MODES.find((m) => m.mode === item.priceMode)!;
  // STORIA-Bug (Mimmo, 07./08.09.2026): Name/Beschreibung klingt nach Portionsgroesse ("fuer N
  // Personen"/Platte), die Zeile steht aber auf "pro Person" — genau dieses Muster liess bei 40
  // Gaesten aus einer eingetragenen Stueckzahl "8" faelschlich "320" werden (8 × Gaestezahl statt
  // 8 Stueck insgesamt). Rein informativ, aendert nichts an der Rechnung — der Bediener entscheidet
  // per `cycleMode`, ob "pro Person" hier wirklich stimmt.
  const stueckzahlVerdacht = item.priceMode === "per_person" && klingtNachPortionsgroesse(item.name, item.description);
  /** Punkt 32: bei „pro Person" hat eine eigene Höchstmenge keine Wirkung (§ 11). */
  const perPersonOhneMenge = item.priceMode === "per_person";
  const qtyField = useBufferedField(milliToQty(item.qtyMilli), (raw) => onPatch({ qtyMilli: qtyToMilli(raw) }));
  // H7: ungültige/mehrdeutige Eingabe (z. B. „1.500") behält den bisherigen Preis statt still
  // auf 0 bzw. 1,50 € zu clampen.
  const priceField = useBufferedField(centsToEuros(item.unitPriceCents), (raw) =>
    onPatch({ unitPriceCents: parseGermanEurosToCents(raw) ?? item.unitPriceCents }),
  );

  /**
   * Katalog-Vorschläge im Bezeichnungsfeld (Betreiber-Wunsch, über Antoine weitergegeben:
   * „je nachdem, was er tippt, soll darunter die Liste kommen"). Das Feld selbst ist unveraendert
   * kontrolliert ueber `item.name` (kein eigener Query-State) — die getippte Bezeichnung IST die
   * Suchanfrage. `katalogOffen` traegt nur, OB die Liste sichtbar sein DARF (Fokus/Tippen setzt
   * sie, ein Klick/Escape/Klick-ausserhalb loescht sie wieder) — geschlossen bei leerem Feld oder
   * ohne Treffer zeigt `katalogVorschlaege` (s.u.) ohnehin nichts, kein Zustand haengt „alt" nach.
   */
  const [katalogOffen, setKatalogOffen] = useState(false);
  const katalogQuery = item.name.trim().toLowerCase();
  // Reuse 1:1: dieselbe Sektions- + Substring-Filterung wie `filteredCatalog` in
  // `PositionHinzufuegenDrawer.tsx`, hier auf `item.name` statt einem eigenen Suchfeld.
  const katalogVorschlaege = useMemo(() => {
    if (!katalogQuery) return [];
    return catalogItems
      .filter((ci) => kindToSection(ci.kind) === section)
      .filter((ci) => ci.name.toLowerCase().includes(katalogQuery) || (ci.description ?? "").toLowerCase().includes(katalogQuery))
      .slice(0, 8);
  }, [catalogItems, section, katalogQuery]);
  const katalogSichtbar = katalogOffen && katalogVorschlaege.length > 0;
  const abgelehnteVorschlaege = useAbgelehnteVorschlaege();
  /** Container aus Bezeichnungsfeld + Dropdown — Ziel fuer die Klick-ausserhalb-Pruefung unten
   *  (`contains`), identisch mit dem Element, das ohnehin schon `data-no-dnd` traegt. */
  const katalogContainerRef = useRef<HTMLDivElement>(null);
  /**
   * Schliessen bei Klick ausserhalb: ref-basiert statt eines viewport-deckenden Overlays (Bugfix
   * 01.09.2026 — das vorherige `fixed inset-0`-Overlay lag laut CSS-Stacking-Regeln ueber JEDEM
   * anderen Bedienelement der Seite, nicht nur ueber dieser Zeile, solange die Liste offen war;
   * siehe Auftrag/Review). Der Listener sitzt bewusst in der BUBBLE-Phase auf `document` (kein
   * `capture: true`): React haengt seinen eigenen `onMouseDown`-Handler eines Vorschlags am
   * App-Root ein, der im DOM-Baum UNTER `document` liegt — in der Bubble-Reihenfolge (Ziel nach
   * oben) feuert der Vorschlag-eigene Handler darum zuerst und trifft seine Auswahl-Entscheidung,
   * BEVOR dieser Listener ueberhaupt laeuft.
   */
  useEffect(() => {
    if (!katalogOffen) return;
    const schliessenBeiAussenklick = (e: MouseEvent) => {
      if (!katalogContainerRef.current?.contains(e.target as Node)) {
        setKatalogOffen(false);
      }
    };
    document.addEventListener("mousedown", schliessenBeiAussenklick);
    return () => document.removeEventListener("mousedown", schliessenBeiAussenklick);
  }, [katalogOffen]);

  /**
   * Builder v3 / B-4 (Entwurf 31a, Anordnung): Die Vorlage zeigt Positionen als ruhige Liste —
   * Bezeichnung, darunter Allergene und Diaet, rechts Rechenweg und Betrag. Bearbeitet wird erst
   * auf Ansage („Alle bearbeiten" im Abschnittskopf).
   *
   * Warum das mehr als Optik ist: Bisher stand jede Position dauerhaft als Formular da — bei einem
   * Angebot mit 20 Positionen sind das 60 Eingabefelder, durch die man den Blick tragen muss, um
   * zu pruefen, was drinsteht. Genau dieses Pruefen ist der haeufigste Vorgang; das Aendern ist
   * der seltene.
   *
   * Es entsteht KEINE zweite Wahrheit: Beide Ansichten lesen dasselbe `item` und dieselbe
   * Rechnung (`lineGross` aus packages/pricing). Das Bearbeitungsformular unten ist unveraendert.
   */
  /** Migration 725: Paket nur für einen Teil der Gäste („3 von 4“) — höchstens alle. */
  const gaeste = item.priceMode === "per_person" && item.guestCount != null && item.guestCount > 0 ? Math.min(item.guestCount, guests) : guests;
  if (!bearbeiten) {
    const { formel, betrag, einzelpreis } = leseZeile(item, gaeste);
    const diaet = activeDietary(lmiv?.dietary);
    // K1.0: vom Gast gewuenscht, im Katalog nicht vorhanden - der Betreiber traegt den Preis nach.
    const preisFehlt = item.isOptional && item.unitPriceCents === 0;
    /** LMIV: fehlt die Angabe (ungepflegt oder ungeprüfter Altbestand), steht das als Hinweis da. */
    const allergeneFehlen = lmivLuecke(lmiv);
    /* „Aus Karte übernehmen?" (Mockup 13.10.2026): eindeutiger Namenstreffer in der Karte. Nur
       vorschlagen — verknüpft wird erst auf Klick; Name und Preis bleiben. Ohne `onOeffnen` ist
       die Variante nicht bearbeitbar (H-12), dann auch kein Vorschlag. */
    const treffer = allergeneFehlen ? kartenTreffer(item, catalogItems) : null;
    const vorschlag = onOeffnen && treffer && !lmivLuecke(treffer) && !abgelehnteVorschlaege.has(item.id) ? treffer : null;
    const werkzeuge = (
      <span className={`flex shrink-0 items-center justify-end gap-[2px] self-start text-[#6F675A] dark:text-secondary-fixed-dim ${werkzeugKlasse}`} data-testid="zeile-werkzeuge">
        {onOeffnen && onNeuerGangAbHier && (
          <Button
            type="button"
            variant="ghost"
            data-no-dnd="true"
            className="!px-1.5 !py-0.5 self-center text-on-surface-variant dark:text-secondary-fixed-dim"
            onClick={onNeuerGangAbHier}
            title="Neuer Gang ab hier"
            aria-label={`Ab ${item.name || "dieser Position"} einen neuen Gang beginnen`}
          >
            <Icon name="call_split" className="text-[16px]" />
          </Button>
        )}
        {/* Migration 384: "Aus Wahlgruppe lösen" - dasselbe H-12-Gate wie Bearbeiten/Loeschen
            (onOeffnen), zusaetzlich nur sichtbar, wenn die Zeile ueberhaupt einer Gruppe
            angehoert. Ruft onPatch({ choiceGroupId: null }) ueber onGruppeVerlassen - kein
            Sonderweg, derselbe Autosave wie jede andere Aenderung. */}
        {onOeffnen && item.choiceGroupId != null && onGruppeVerlassen && (
          <Button
            type="button"
            variant="ghost"
            data-no-dnd="true"
            className="!px-1.5 !py-0.5 self-center text-on-surface-variant dark:text-secondary-fixed-dim"
            onClick={onGruppeVerlassen}
            title="Aus Wahlgruppe lösen"
            aria-label={`${item.name || "Position"} aus Wahlgruppe lösen`}
          >
            <Icon name="link_off" className="text-[16px]" />
          </Button>
        )}
        {/* B-4 (Entwurf 31b): der Weg in die Bearbeitung DIESER Zeile. Fehlt `onOeffnen`, ist die
            Variante nicht bearbeitbar (H-12) — dann gibt es auch keinen Knopf, der so tut. */}
        {onOeffnen && (
          <Button
            type="button"
            variant="ghost"
            data-no-dnd="true"
            className="!px-1.5 !py-0.5 self-center text-on-surface-variant dark:text-secondary-fixed-dim"
            onClick={onOeffnen}
            title="Zeile bearbeiten"
            aria-label={`${item.name || "Position"} bearbeiten`}
          >
            <Icon name="edit" className="text-[16px]" />
          </Button>
        )}
        {/* Punkt 3 (Antoine 20.08.2026): direkter Ein-Klick-Zugang zum Loeschen — dieselbe
            `onRemove` wie im Formular (inkl. Undo-Toast), gleiches H-12-Gate wie der Stift. */}
        {onOeffnen && (
          <Button
            type="button"
            variant="ghost"
            data-no-dnd="true"
            className="!px-1.5 !py-0.5 self-center text-on-surface-variant transition-colors hover:text-error dark:text-secondary-fixed-dim dark:hover:text-[#e39b92]"
            onClick={onRemove}
            title="Position entfernen"
            aria-label={`${item.name || "Position"} entfernen`}
          >
            <Icon name="delete" className="text-[16px]" />
          </Button>
        )}
      </span>
    );
    if (paketKopf) {
      /* Mockup v3: der Paket-Kopf. Die Zahlen sind dieselben wie in jeder Zeile (`leseZeile`,
         also `lineGross` aus packages/pricing) — hier nur als Kopf gesetzt: Preis pro Gast gross,
         darunter der Rechenweg „pro Gast · × 20 = 500,00 €". */
      return (
        <div
          ref={setNodeRef}
          style={dragStyle}
          data-testid={`builder-item-row-${item.id}`}
          data-paket-kopf="true"
          onClick={antippen}
          className={`group/zeile flex flex-col gap-3 border-b border-[#F6F0E4] px-[18px] py-[14px] sm:flex-row sm:items-start sm:gap-4 transition-colors hover:bg-[#FBF6EC] dark:border-outline/20 dark:hover:bg-surface-variant/15 ${rowDragClass} ${isDragging ? "relative z-10 rounded-lg bg-surface-container-low opacity-90 dark:bg-surface-variant/20" : ""}`}
          {...dndHandlers}
        >
          <div className="min-w-0 flex-1">
            <StatusPill meta={{ label: "Paket", tone: "primary" }} icon="inventory_2" />
            <p className="mt-2 font-headline-sm text-[16px] font-semibold text-on-background dark:text-inverse-on-surface">
              {item.name || "(ohne Bezeichnung)"}
            </p>
            {item.description?.trim() && (
              <p className="mt-1 text-[12.5px] leading-[1.5] text-on-surface-variant dark:text-secondary-fixed-dim">{item.description}</p>
            )}
            {item.priceMode === "per_person" && guests > 1 && (
              /* Migration 725 (Mockup freigegeben 15.10.2026): „für [3] von 4 Gästen“, leer = alle. */
              <p className="mt-2 flex items-center gap-1.5 text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim" data-no-dnd="true" onClick={(e) => e.stopPropagation()}>
                für
                {onOeffnen ? (
                  <TeilgruppeEingabe item={item} guests={guests} onPatch={onPatch} />
                ) : (
                  <span className="tabular-nums">{gaeste}</span>
                )}
                von {guests} Gästen
              </p>
            )}
            {onOeffnen && <MengeAlsGaesteHinweis item={item} guests={guests} onPatch={onPatch} />}
            {paketKopf.enthalten && (
              <p className="mt-2 flex items-center gap-1.5 text-[12px] text-[#6F675A] dark:text-[#b8ae9e]" data-testid="paket-enthalten">
                <Icon name="check_circle" className="text-[15px]" /> {paketKopf.enthalten}
              </p>
            )}
            {paketKopf.aufteilung && (
              /* Punkt R (Mockup freigegeben 13.10.2026): die USt-Aufteilung des Pakets, je Teil eine Zeile. */
              <div className="mt-2 max-w-md" data-testid="paket-aufteilung-zeilen">
                {paketKopf.aufteilung.teile.map((t) => (
                  <div key={t.rateBp} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-3 text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim">
                    <span>{t.anteil === "getraenke" ? "davon Getränke" : "davon Speisen"}</span>
                    <span className="tabular-nums">
                      {t.perPersonCents != null ? `${centsToEuros(t.perPersonCents)} € × ${gaeste} · ` : ""}
                      {t.rateBp / 100} %
                    </span>
                    <span className="text-right tabular-nums">{money(t.grossCents)}</span>
                  </div>
                ))}
                <span className="mt-1 inline-block rounded-full bg-[#F0EAE0] px-2 py-0.5 text-[11px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                  {paketKopf.aufteilung.ausPaket ? "aus dem Paket" : "geschätzt 70/30"}
                </span>
              </div>
            )}
          </div>
          <div className="shrink-0 sm:text-right">
            <p className="text-beleg-summe tabular-nums text-on-background dark:text-inverse-on-surface">{einzelpreis ?? betrag}</p>
            <p className="text-beleg-rechenweg text-on-surface-variant dark:text-secondary-fixed-dim">
              {item.priceMode === "per_person" ? `pro Gast · × ${gaeste} = ${betrag}` : item.priceMode === "flat" ? "Pauschale für die Veranstaltung" : `${formel} = ${betrag}`}
            </p>
          </div>
          {werkzeuge}
        </div>
      );
    }
    /* B-5 (31c): Der Zahlenblock rechts steht auf `whitespace-nowrap` — Rechenweg, Betrag,
       Warn-Chips und der Stift belegen zusammen rund 260 px. Bei 390 px blieben fuer die
       Bezeichnung keine 100 px, jeder etwas laengere Gerichtname war ein Wortsalat. Am Handy
       steht die Bezeichnung darum in ihrer eigenen Zeile und die Zahlen rechtsbuendig darunter;
       ab 640 px wieder nebeneinander wie im Entwurf 31a. Es aendert sich die Anordnung, nichts
       am Inhalt. */
    return (
      /* `ref`/`style` (Verschiebe-Transform) + die dnd-kit-`attributes`/gefilterten `listeners`
         sitzen jetzt direkt am aeusseren Zeilen-Wrapper (siehe Kommentar oben) — die ganze Zeile
         ist der Griff. `isDragging` hebt die Zeile waehrend des Ziehens leicht ab, damit
         erkennbar bleibt, WAS gerade verschoben wird. */
      <div
        ref={setNodeRef}
        style={dragStyle}
        data-testid={`builder-item-row-${item.id}`}
        /*
          Punkt 75, Schritt 3 (Entwurf 41a): die Zeilenfarben und -abstaende aus dem Entwurf.
          Weisser Grund, beim Darueberfahren der waermste Ton der Palette, die leichtere der
          beiden Trennlinien. Die dunklen Marken bleiben daneben stehen — der Entwurf kennt nur
          den hellen Modus, und ohne sie waere die Liste im dunklen unlesbar.

          Entwurf 43: die Zeile IST jetzt das Raster (`RASTER_43`) — dieselbe Definition, die
          auch die Kopfzeile benutzt. Vorher baute sie ihre Spalten mit festen Breiten daneben
          nach, und die Spaltennamen standen 16 px neben ihren Spalten.
        */
        onClick={antippen}
        className={`group/zeile ${RASTER_43} items-start border-b border-[#F6F0E4] bg-white py-[10px] transition-colors hover:bg-[#FBF6EC] dark:border-outline/20 dark:bg-transparent dark:hover:bg-surface-variant/15 ${rowDragClass} ${isDragging ? "relative z-10 rounded-lg bg-surface-container-low opacity-90 dark:bg-surface-variant/20" : ""}`}
        {...dndHandlers}
      >
        {/*
          SPALTE 1 — das Griff-Zeichen, sechs Punkte in der hellsten Farbe der Palette.

          Mit der Maus ist es reine Anzeige, der Griff BLEIBT die ganze Zeile. Mit dem Finger
          (15.10.2026) ist NUR diese Spalte der Griff, sonst verschob jedes Scrollen die Position.
          Der Griff BLEIBT mit der Maus die ganze Zeile — das war eine bewusste
          Entscheidung (die frühere Fassung mit separatem Griff plus Pfeilen machte die Ansicht
          „sehr unruhig", und man musste den Griff exakt treffen). Das Zeichen sagt nur, DASS man
          ziehen kann; wo man anfasst, ändert sich nicht. Deshalb auch `aria-hidden`: für die
          Tastatur gibt es den eigenen Weg, den `dndHandlers` mitbringt.

          Die Spalte steht AUCH ohne Griff (nicht umsortierbare Varianten) — sonst rutschte die
          ganze Zeile 42 px nach links und stünde wieder neben ihrer Kopfzeile.
        */}
        <span
          aria-hidden
          data-drag-griff={umsortierbar ? "true" : undefined}
          className={`flex items-center justify-center self-stretch text-[#DDD4C2] dark:text-outline/40 ${umsortierbar ? "select-none [-webkit-touch-callout:none] [@media(hover:none)]:-ml-[18px] [@media(hover:none)]:pl-[18px] [@media(hover:none)]:text-[#A39885]" : ""}`}
        >
          {inWahlgruppe ? (
            /* Entwurf 43: der Radio-Ring einer Alternative — 16 px, 1,5 px Rand in der
               Icon-Farbe der Vorlage. Immer LEER: welche Alternative es wird, entscheidet der
               Gast im Dokument, nicht der Betreiber hier. Ein gefuellter Ring behauptete eine
               Entscheidung, die noch niemand getroffen hat. */
            <span className="h-[16px] w-[16px] shrink-0 rounded-full border-[1.5px] border-[#C9BFAE] bg-white dark:border-outline/50 dark:bg-transparent" />
          ) : (
            umsortierbar && (
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="6" cy="4" r=".9" />
                <circle cx="10" cy="4" r=".9" />
                <circle cx="6" cy="8" r=".9" />
                <circle cx="10" cy="8" r=".9" />
                <circle cx="6" cy="12" r=".9" />
                <circle cx="10" cy="12" r=".9" />
              </svg>
            )
          )}
        </span>
        {/* SPALTE 2 — Position: Titel und Beschreibung als ZWEI Zeilen (Entwurf 43). */}
        <div className="min-w-0">
          {/* Entwurf 41a: 12,5 px, mittlere Staerke, einzeilig mit „…" statt Umbruch. */}
          <p className="break-words text-[12.5px] font-medium text-[#262119] dark:text-inverse-on-surface">
            {item.name || <span className="font-normal text-on-surface-variant/60">(ohne Bezeichnung)</span>}
            {/*
              Punkt 32, Konzept § 6.2: aus dem grauen „optional" wird ein sprechendes „zubuchbar"
              in der Primärfarbe. Grau und wortkarg las sich die Zeile wie ein Restzustand;
              tatsächlich ist es das Einzige, was der Gast selbst entscheiden darf.

              ABWEICHUNG VOM KONZEPT, mit Grund: dort sollte der Chip den Betrag in Klammern
              mittragen („dieselbe Schreibweise wie das Kundendokument"). Im Dokument ist das
              richtig — dort gibt es keine Zahlenspalte. Hier steht der Betrag zwei Handbreit
              weiter rechts bereits in der Zeile; ein zweites Mal im Chip ist keine Klarheit,
              sondern eine zweite Stelle, die beim nächsten Umbau auseinanderlaufen kann. Eine
              bestehende Prüfung hat das sofort gemeldet (zwei Treffer für „99,00" in derselben
              Zeile) — und sie hatte recht.

              OHNE Preis bleibt der Chip grau und heißt weiter „optional": genau dann KANN der
              Gast nichts dazubuchen (die Karte „Vor dem Senden" nennt diesen Fall beim Namen),
              und ein farbiger Chip würde eine Bedienbarkeit behaupten, die es nicht gibt.
            */}
            {item.isOptional && (
              <span
                className={
                  item.unitPriceCents > 0
                    ? "ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-beleg-mikro text-primary dark:bg-primary-fixed-dim/10 dark:text-primary-fixed-dim"
                    : "ml-2 rounded bg-surface-container-high px-1.5 py-0.5 text-beleg-mikro text-on-surface-variant dark:bg-white/[0.08] dark:text-[#b8ae9e]"
                }
              >
                {item.unitPriceCents > 0 ? "zubuchbar" : "optional"}
              </span>
            )}
          </p>
          {/*
            Die Beschreibung stand in der Leseansicht bisher NIRGENDS — sie liess sich eingeben
            und war danach nur im Bearbeitungsformular wiederzufinden. Der Entwurf zeigt sie als
            zweite Zeile unter dem Titel, und damit ist sie zurueck, wo sie hingehoert: auf dem
            Bildschirm, auf dem der Betreiber sein Angebot prueft. Ein Titel wie „Antipasto
            misto" sagt ohne den Zusatz nicht, was auf dem Teller liegt.

            `title`: bei langen Beschreibungen kuerzt „…" — der volle Text bleibt im Tooltip.
          */}
          {item.description?.trim() && (
            <p
              className="truncate text-[11.5px] leading-[1.45] text-[#6F675A] dark:text-secondary-fixed-dim"
              title={item.description}
            >
              {item.description}
            </p>
          )}
        </div>
        {/*
          SPALTE 3 — Kennzeichen. Im Entwurf eine eigene, 170 px breite Spalte; bisher standen
          die Allergene unter dem Titel und schoben jede Zeile unterschiedlich hoch. Untereinander
          sind sie vergleichbar, und genau dafuer liest man sie.
        */}
        {(item.section === "menu" || item.section === "drinks") ? (
          <span className="col-start-2 mt-[4px] flex min-h-[18px] min-w-0 flex-wrap items-center gap-[5px] sm:col-start-auto sm:mt-0">
            {/*
              Punkt 75, Schritt 2 (Befund ④): `dietary` wird hier NICHT mehr mitgegeben. Die
              Ernaehrungsform stand doppelt in derselben Zeile — einmal als farbiger Kurz-Chip
              („Veg") aus den Kennzeichen, und direkt daneben noch einmal ausgeschrieben
              („vegetarisch"). Der Entwurf nennt genau das: Kennzeichen uneinheitlich, liest
              sich wie ein Fehler. Es bleibt die ausgeschriebene Form — sie ist die
              verstaendlichere von beiden und braucht keine Farbe, um gelesen zu werden.
            */}
            {/* Mockup 13.10.2026 („ruhige Fassung"): Hinweise grau mit Symbol, keine Bernstein-Pille
                mehr. Der Satz steht zusätzlich in der Prüfliste rechts. */}
            {allergeneFehlen ? (
              <span className={HINWEIS} title="Allergene ungepflegt – bitte im Katalog prüfen (LMIV)">
                <Icon name="info" className="text-[15px]" />
                {item.catalogItemId == null && !treffer && catalogItems.length > 0
                  ? "Allergene fehlen · kein passender Karteneintrag"
                  : "Allergene fehlen"}
              </span>
            ) : (
              <>
                <AllergenBadges allergens={lmiv ? lmiv.allergens : null} checked={lmiv?.allergensCheckedAt != null} />
                {item.catalogItemId != null && (
                  <span className={HINWEIS}>
                    <Icon name="link" className="text-[15px]" />
                    aus der Karte
                  </span>
                )}
              </>
            )}
            {diaet.map((d) => (
              <StatusPill key={d.label} meta={{ label: d.label.toLowerCase(), tone: "success" }} icon="eco" />
            ))}
          </span>
        ) : (
          /* Equipment, Personal, Sonstiges kennen keine Allergene. Die Spalte bleibt trotzdem
             stehen — sonst haette jeder Abschnitt ein anderes Tabellenbild, und Antoine hat
             ausdruecklich verlangt, dass Equipment und Personal MITZIEHEN. */
          <span className="hidden sm:block" />
        )}
        {/*
          SPALTEN 4-7 — Menge, Einzelpreis, Summe, Aktionen.

          Ab 640 px löst sich dieser Behälter auf (`sm:contents`) und seine vier Kinder werden
          selbst zu Rasterfeldern; darunter bleibt er ein umbrechender Verbund in einer eigenen
          Zeile. So gibt es EINE Auszeichnung für beide Breiten statt zweier Darstellungen, die
          auseinanderlaufen können.
        */}
        <div className="col-start-2 mt-[6px] flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1 sm:col-start-auto sm:mt-0 sm:contents">
          {/* Die Preis-Warnungen muessen in der LESE-Ansicht stehen, nicht nur im Formular: Sie
              sind Pruef-Hinweise, und Pruefen ist genau das, wofuer diese Ansicht da ist. Fielen
              sie hier weg, waere die ruhigere Darstellung mit einem Informationsverlust erkauft. */}
          {/* „geändert" / „starke Abweichung" / „kein Preis" standen hier bis zum 17.09.2026.
              Antoine, nach den Rückmeldungen des Betreibers: „Das ist ein ganz üblicher und
              normaler Vorgang in der Gastronomie […] nimm alle diese Warnungen weg […] der
              Gastronom weiß schon, was er da tut." Ein Preis, der vom Katalog abweicht, ist eine
              Entscheidung — und ein Angebot ist der Ort, an dem sie getroffen wird. */}
          {nachKarte ? (
            <span className="justify-self-end whitespace-nowrap text-beleg-rechenweg text-on-surface-variant/70 dark:text-secondary-fixed-dim sm:col-span-3" data-testid="position-nach-karte">
              nach Karte
            </span>
          ) : preisFehlt ? (
            /* Ein Betrag von 0,00 EUR waere hier eine Luege: Die Position ist gewuenscht, nur
               noch nicht bepreist, und sie zaehlt bewusst nicht in die Summe. */
            /* Über die drei Zahlenspalten hinweg und rechtsbündig — dort, wo sonst der Betrag
               stünde. Sonst säße der Hinweis in der 64 px schmalen Mengenspalte. */
            <span className="justify-self-end whitespace-nowrap rounded bg-[#F0EAE0] px-2 py-0.5 text-beleg-notiz text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e] sm:col-span-3">
              Preis fehlt
            </span>
          ) : imPaket ? (
            /* Mockup v3: im Paketpreis enthalten — kein „20× à 0,00 €". */
            <span className="justify-self-end whitespace-nowrap text-beleg-rechenweg text-on-surface-variant/70 dark:text-secondary-fixed-dim sm:col-span-3" data-testid="im-paket">
              im Paket
            </span>
          ) : kollabiert ? (
            /*
             * Die Sektion hat eine Pauschale — diese Zeile zaehlt nicht fuer sich (die Engine
             * setzt ihren `grossCents` auf 0, siehe `istSektionsKern`). Also steht hier auch kein
             * Betrag.
             *
             * Antoine, 17.09.2026: „Pauschalpreis Gesamt bei Blumen 250.- €, trotzdem steht unten
             * 2x 500.- €." Genau das war der Zustand: die Zeile rechnete sichtbar ihren eigenen
             * Preis aus, waehrend die Sektion darueber eine ganz andere Zahl auswies. Zwei Zahlen
             * auf einem Bildschirm, von denen nur eine zaehlt.
             *
             * Bewusst OHNE Durchstreichung und ohne „zählt nicht"-Abzeichen: beides hatte Antoine
             * am selben Tag ausdruecklich abgeraeumt („nimm alle diese Warnungen weg und diese
             * Hinweise und dass durchgestrichen ist der Einzelpreis"). Es bleibt der Leistungstext
             * und der ruhige Hinweis, WO der Preis steht — dieselbe Loesung wie im Kundendokument,
             * das an dieser Stelle „—" schreibt.
             */
            <span className="justify-self-end whitespace-nowrap text-beleg-rechenweg text-on-surface-variant/60 sm:col-span-3">in der Pauschale</span>
          ) : (
            <>
              {/*
                SPALTE 4 — Menge. NUR die Menge.

                Hier stand nacheinander ein Schloss (Punkt 75, Befund ⑥) und danach „35× ≠ 18"
                (Entwurf 43) — beides sollte erklaeren, dass eine Menge fest ist und nicht mit
                der Gaestezahl waechst. Antoine am 22.09.2026 dazu: „Das kann der Betreiber ganz
                alleine entscheiden, was er da macht. Da braucht es keinen Hinweis. So hatten wir
                es ja auch besprochen gehabt." — dieselbe Linie wie am 20.09.2026 („nimm alle
                diese Warnungen weg und diese Hinweise").

                Eine feste Menge ist der Normalfall, kein Sonderfall: 35 Gedecke bei 18 Gaesten,
                weil dreimal eingedeckt wird. Wer sie eintraegt, weiss warum. Die Zeile sagt
                deshalb, WAS bestellt ist, und kommentiert es nicht.

                `tabular-nums` (Entwurf): Ziffern gleicher Breite, damit die Mengen untereinander
                stehen statt zu flattern.
              */}
              <span className="flex items-baseline justify-end gap-[4px] whitespace-nowrap text-[12.5px] font-medium tabular-nums text-[#262119] dark:text-secondary-fixed-dim">
                {pauschaleBlock && item.surcharge === true && !item.isOptional && <span className="text-on-surface-variant/70">zzgl.</span>}
                {formel}
              </span>
              {/* SPALTE 5 — Einzelpreis. Bughunt 27.08.2026: "50× 745,00 €" ohne Einzelpreis
                  liest sich wie eine falsche Rechnung — dieser Zusatz sagt, dass 745,00 € der
                  Zeilen-GESAMTBETRAG ist. Die Spalte steht auch leer da, sonst rutschte die
                  Summe je Zeile woanders hin. */}
              <span className="whitespace-nowrap text-[12.5px] tabular-nums text-[#57503F] dark:text-secondary-fixed-dim sm:text-right">
                {einzelpreis ? `à ${einzelpreis}` : ""}
              </span>
              {/* SPALTE 6 — Summe. */}
              <span className="whitespace-nowrap text-[12.5px] font-semibold tabular-nums text-[#262119] dark:text-inverse-on-surface sm:text-right">
                {betrag}
              </span>
            </>
          )}
        {/*
          SPALTE 7 — die Aktionen (`werkzeuge` oben). Bis 28.09.2026 standen sie dauerhaft
          (Antoine 20.09.2026: „NICHTS DARF WEG SEIN"). Die freigegebene Neugestaltung (Mockup v3,
          Antoine 28.09.2026) zeigt sie beim Darüberfahren und bei Tastaturfokus; ohne Zeiger
          öffnet ein Tippen auf die Zeile das Bearbeiten. Es fehlt keine Bedienung — sie ist
          nur nicht mehr dauernd im Bild.
        */}
        {werkzeuge}
        </div>
        {vorschlag && (
          <div
            data-no-dnd="true"
            data-testid={`allergene-vorschlag-${item.id}`}
            className="col-span-full mt-[6px] flex flex-wrap items-center gap-x-[10px] gap-y-2 rounded-[9px] bg-primary/[0.07] px-[10px] py-2 text-[12px] text-[#262119] dark:bg-primary/15 dark:text-inverse-on-surface"
          >
            <div className="min-w-0 flex-[1_1_240px]">
              In der Karte gefunden: <b>{vorschlag.name}</b> · Allergene{" "}
              <AllergenBadges allergens={vorschlag.allergens} checked={vorschlag.allergensCheckedAt != null} />
              {vorschlag.priceCents !== item.unitPriceCents && (
                <small className="block text-[11.5px] text-[#6F675A] dark:text-secondary-fixed-dim">
                  Preis in der Karte {centsToEuros(vorschlag.priceCents)} €. Dein Preis {centsToEuros(item.unitPriceCents)} € bleibt.
                </small>
              )}
            </div>
            {/* Nur die Verknüpfung — Name und Preis bleiben (Beträge nie überschreiben). */}
            <Button type="button" className="!px-3 !py-1.5" onClick={() => onPatch({ catalogItemId: vorschlag.id })}>
              Übernehmen
            </Button>
            <Button type="button" variant="ghost" className="!px-3 !py-1.5" onClick={() => vorschlagAblehnen(item.id)}>
              Nein
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      data-testid={`builder-item-row-${item.id}`}
      className={`flex items-start gap-0.5 ${rowDragClass} ${isDragging ? "relative z-10 rounded-lg bg-surface-container-low opacity-90 dark:bg-surface-variant/20" : ""}`}
      {...dndHandlers}
    >
      <div className="min-w-0 flex-1 px-3 py-2.5">
      <div className="flex items-center gap-2">
        {/* Katalog-Vorschläge: kein eigenes Suchfeld (anders als `TextbausteinPicker.tsx`, dem
            strukturellen Vorbild fuer Positionierung/`role="listbox"`/Schliess-Verhalten) — das
            BESTEHENDE Bezeichnungsfeld selbst ist der Ausloeser. Das aeussere `data-no-dnd`
            deckt Feld UND Dropdown ab (`closest()` findet es ueber jeden Nachkommen), noetig
            damit ein Klick/Drag-Start hier nicht als Drag-Start der ganzen Zeile missverstanden
            wird. Dasselbe Element traegt `katalogContainerRef` fuer die Klick-ausserhalb-Pruefung
            (s.u.) — kein zusaetzliches Element noetig. */}
        <div ref={katalogContainerRef} className="relative min-w-0 flex-1" data-no-dnd="true">
          <Input
            className="w-full !py-1.5"
            placeholder="Bezeichnung"
            value={item.name}
            onChange={(e) => {
              onPatch({ name: e.target.value });
              setKatalogOffen(true);
            }}
            onFocus={() => setKatalogOffen(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setKatalogOffen(false);
            }}
            autoComplete="off"
            aria-expanded={katalogSichtbar}
          />
          {katalogSichtbar && (
            // Klick ausserhalb schliesst ueber den `document`-Listener oben (kein `onBlur` — das
            // wuerde die Liste VOR dem Klick auf einen Vorschlag entfernen und den Klick damit
            // vereiteln). Kein viewport-deckendes Overlay-Element mehr noetig/vorhanden.
            <div
              role="listbox"
              aria-label="Katalog-Vorschläge"
              className="absolute left-0 top-[calc(100%+4px)] z-50 max-h-[240px] w-full min-w-[220px] overflow-y-auto rounded-lg border border-outline-variant/40 bg-surface-container-lowest py-1 shadow-xl dark:border-outline/30 dark:bg-night-card"
            >
              {katalogVorschlaege.map((ci) => (
                <button
                  key={ci.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  // `onMouseDown` + `preventDefault` statt `onClick`: verhindert, dass der
                  // Klick dem Feld den Fokus entzieht ("Feld bleibt fokussiert" laut Auftrag) —
                  // ohne `preventDefault` wuerde der Browser den Fokus vor der Auswahl auf
                  // diesen Knopf verschieben. Feuert ausserdem VOR dem `document`-Listener oben
                  // (Bubble-Reihenfolge, s. Kommentar dort), die Auswahl ist also nie ein Wettlauf.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    // Name + Verknuepfung (Allergene aus der Karte), Preis bleibt — siehe Props-Doku oben.
                    onPatch({ name: ci.name, catalogItemId: ci.id });
                    setKatalogOffen(false);
                  }}
                  className="block w-full truncate px-3 py-1.5 text-left text-body-sm text-on-surface hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-white/5"
                >
                  {ci.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button type="button" variant="ghost" data-no-dnd="true" className="!px-1.5 text-on-surface-variant" onClick={onRemove} title="Entfernen">
          <Icon name="close" className="text-[16px]" />
        </Button>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {/*
          Bei `pauschal` wirkt die Menge nicht — das Feld ist deshalb ABGESCHALTET statt nur unten
          erklaert. Antoine, 17.09.2026: „Ist das pauschal, unten, wenn man auf bearbeiten geht,
          nicht überflüssig?" Die frühere Fusszeile „Pauschal (Menge ohne Wirkung) · 250,00 €"
          wiederholte den Modus-Knopf und das Preisfeld; die eine Angabe darin, die sonst nirgends
          stand, war der wirkungslose Faktor. Ein graues Feld sagt das an der Stelle, an der es
          zaehlt, und braucht keinen Satz darunter.

          Ausgeblendet wird es bewusst NICHT: ein Feld, das beim Moduswechsel verschwindet und
          wiederkommt, ist genau der Fehler, den Antoine an der Knopfreihe kritisiert hat.
        */}
        <Input
          data-no-dnd="true"
          className="!w-16 !py-1 text-right text-body-sm disabled:opacity-40"
          disabled={item.priceMode === "flat"}
          title={item.priceMode === "flat" ? "Bei „pauschal“ wirkt die Menge nicht — der Preis daneben ist der ganze Betrag." : modeMeta.hint}
          {...qtyField}
        />
        <span className={item.priceMode === "flat" ? "text-on-surface-variant/25" : "text-on-surface-variant/60"}>×</span>
        <div className="relative">
          <Input
            data-no-dnd="true"
            className="!w-24 !py-1 pr-6 text-right text-body-sm"
            title="Einzelpreis (brutto)"
            {...priceField}
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-body-sm text-on-surface-variant">€</span>
        </div>
        <button type="button" data-no-dnd="true" onClick={cycleMode} className="rounded border border-outline-variant/50 px-2 py-1 font-label-md text-[11px] text-on-surface-variant hover:border-primary/40 dark:border-outline/40" title={modeMeta.hint}>
          {modeMeta.label}
        </button>
        {stueckzahlVerdacht && (
          <span
            className="rounded bg-[#F0EAE0] px-1.5 py-0.5 font-label-md text-[10px] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
            title='Klingt nach einer Portionsgröße ("für N Personen"/Platte) — prüfen, ob „pro Person" hier wirklich stimmt, oder auf „pro Stück" umstellen.'
          >
            Menge prüfen?
          </span>
        )}
        <button type="button" data-no-dnd="true" onClick={cycleVat} className="rounded border border-outline-variant/50 px-2 py-1 font-label-md text-[11px] text-on-surface-variant hover:border-primary/40 dark:border-outline/40" title="MwSt">
          {vatLabel(item.vatRateBp)}
        </button>
        {/*
          „Optional" war bis zum 17.09.2026 NUR ANZEIGE. Gesetzt wurde das Feld beim Anlegen
          (immer `false`), vom Equipment-Vorschlag (`true`) und beim Paket-Import — zuruecknehmen
          konnte man es nirgends. Der Betreiber sass genau darin fest: „Ich kann aber nirgendwo
          'Optional' aktivieren oder deaktivieren." Seine Blumenzeile ueber 250,00 EUR trug das
          Flag, zaehlte deshalb in KEINEM Preismodus mit, und es gab keinen Weg zurueck. Von aussen
          sah das aus wie ein Rechenfehler — es war eine Eigenschaft ohne Bedienung.

          Eine Eigenschaft, die eine Position aus dem Gesamtpreis nimmt, gehoert dorthin, wo die
          Position bearbeitet wird: neben Menge, Preis, Einheit und MwSt. `aria-pressed` macht den
          Zustand auch ohne Farbe lesbar, wie bei den Preismodell-Umschaltern.
        */}
        {pauschaleBlock ? (
          /*
            Migration 722 (Antoine 14.10.2026, Fall Storia: Käseplatte und Obst zur Menü-Pauschale).
            In einem Block mit Pauschale gibt es drei Stufen, die sich ausschließen:
            inklusive = in der Pauschale enthalten, zzgl. = fest bestellt und zusätzlich berechnet
            (Rabatt gilt), zubuchbar = der Gast entscheidet (ohne Rabatt).
          */
          <div role="group" aria-label="Wie zählt die Position?" data-no-dnd="true" className="inline-flex overflow-hidden rounded border border-outline-variant/50 dark:border-outline/40">
            {(
              [
                ["inklusive", { isOptional: false, surcharge: false }, "In der Pauschale enthalten — kein eigener Betrag."],
                ["zzgl.", { isOptional: false, surcharge: true }, "Fest bestellt: wird zusätzlich zur Pauschale berechnet, der Angebotsrabatt gilt auch hier."],
                ["zubuchbar", { isOptional: true, surcharge: false }, "Der Gast entscheidet beim Annehmen — zählt erst, wenn er sie wählt, ohne Rabatt."],
              ] as const
            ).map(([label, patch, title]) => {
              const aktiv = (item.isOptional === true) === patch.isOptional && (item.surcharge === true && item.isOptional !== true) === patch.surcharge;
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={aktiv}
                  onClick={() => onPatch(patch)}
                  title={title}
                  className={aktiv ? "bg-primary/10 px-2 py-1 font-label-md text-[11px] text-primary" : "px-2 py-1 font-label-md text-[11px] text-on-surface-variant hover:bg-primary/5"}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : (
          <button
            type="button"
            data-no-dnd="true"
            aria-pressed={item.isOptional === true}
            onClick={() => onPatch({ isOptional: !(item.isOptional === true) })}
            className={
              item.isOptional === true
                ? "rounded border border-primary/60 bg-primary/10 px-2 py-1 font-label-md text-[11px] text-primary"
                : "rounded border border-outline-variant/50 px-2 py-1 font-label-md text-[11px] text-on-surface-variant hover:border-primary/40 dark:border-outline/40"
            }
            title={
              item.isOptional === true
                ? "Zubuchbar: Der Gast entscheidet beim Annehmen, ob das dazukommt — die Position zählt NICHT in den Gesamtpreis, sondern kommt erst obendrauf, wenn er sie wählt. Klicken macht sie verbindlich."
                : "Verbindlich: zählt in den Gesamtpreis. Klicken macht sie zubuchbar — dann entscheidet der Gast."
            }
          >
            zubuchbar
          </button>
        )}
        {/*
          ═══ Punkt 32, Konzept § 6.1b: „Höchstmenge je Gast" ═══

          Erscheint nur, wenn die Position zubuchbar ist — sonst stünde hier ein Feld ohne
          Gegenstand. Voreinstellung 1: der Gast sieht dann ein Häkchen und sonst nichts, also
          genau das Verhalten von vor der Mengen-Entscheidung. Erhöht der Betreiber den Wert,
          bekommt der Gast einen Mengenwähler.

          Bei „pro Person" AUSGEGRAUT statt versteckt: die Menge ergibt sich dort aus der
          Gästezahl, das Feld hätte keine Wirkung. Ein Feld, das ohne Erklärung verschwindet,
          liest sich als Fehler — eines mit Begründung als Regel.
        */}
        {item.isOptional === true && (
          <label className="flex items-center gap-1.5" title={perPersonOhneMenge ? MENGE_AUS_GAESTEZAHL : "Wie oft der Gast diese Position höchstens dazubuchen darf. 1 = ein Häkchen, mehr = ein Mengenwähler."}>
            <span className="font-label-md text-[11px] text-on-surface-variant/70">höchstens</span>
            <Input
              type="number"
              min={1}
              max={99}
              // Eigener Name, weil die Zeile ein zweites Zahlenfeld traegt (die Menge): ohne ihn
              // sind beide fuer eine Vorleseansicht — und fuer jede Pruefung — dasselbe „1".
              aria-label="Höchstmenge je Gast"
              data-no-dnd="true"
              disabled={perPersonOhneMenge}
              className="w-14 !px-1.5 !py-0.5 text-center text-[11px]"
              value={String(item.addonMaxQty ?? 1)}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                // Aus einem leeren oder unsinnigen Feld wird die Voreinstellung, nicht `null`:
                // eine Zubuchung ohne Höchstmenge gibt es nicht, und eine 0 hiesse „gar nicht
                // buchbar" — das sagt man mit dem Schalter daneben, nicht mit einer Zahl.
                onPatch({ addonMaxQty: Number.isFinite(n) && n >= 1 ? Math.min(99, n) : 1 });
              }}
            />
            <span className="font-label-md text-[11px] text-on-surface-variant/70">×</span>
          </label>
        )}
      </div>
      {perPersonOhneMenge && item.isOptional === true && (
        <p className="mt-1 font-body-sm text-[11px] text-on-surface-variant/60">{MENGE_AUS_GAESTEZAHL}</p>
      )}
      {/*
        ═══ sql/751–753: Art der Zubuchung ═══
        „Ersetzt das Hauptmenü“: wählt der Gast z. B. 3 × vegetarisch, essen 3 Gäste weniger das Hauptmenü
        (Menge und Betrag sinken, vom Server aus der beim Versand eingefrorenen Tabelle). Nicht bei
        „pauschal“ — dort gibt es keine Gästezahl, die abgezogen werden könnte.
        „Mindestumsatz inkl. Menü“: nur bei „pauschal“; gebucht wird die Aufstockung auf den Betrag.
        „Mit … verbunden“: zwei Positionen, die der Gast nur zusammen buchen kann (ein Schalter).
      */}
      {item.isOptional === true && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-no-dnd="true">
          {item.priceMode !== "flat" && (
            <label className="flex items-center gap-1.5 font-body-sm text-[12px] text-on-surface-variant">
              <Checkbox
                checked={item.addonErsetztHauptmenue === true}
                aria-label="Ersetzt das Hauptmenü für diese Gäste"
                onChange={(e) =>
                  onPatch(
                    e.target.checked
                      ? {
                          addonErsetztHauptmenue: true,
                          // Ein Zähler braucht Platz bis zur Gästezahl; „pro Person“ ersetzt ohnehin alle.
                          ...(item.priceMode === "per_unit" && (item.addonMaxQty ?? 1) <= 1 ? { addonMaxQty: Math.min(99, Math.max(2, guests)) } : {}),
                        }
                      : { addonErsetztHauptmenue: false },
                  )
                }
              />
              Ersetzt das Hauptmenü für diese Gäste
            </label>
          )}
          {item.priceMode === "flat" && (
            <label className="flex items-center gap-1.5 font-body-sm text-[12px] text-on-surface-variant">
              Art
              <Select
                className="!w-auto !py-0.5 !text-[12px]"
                ariaLabel="Art der Zubuchung"
                value={item.addonArt ?? ""}
                onValueChange={(v) => onPatch({ addonArt: v === "mindestumsatz" ? "mindestumsatz" : null })}
                options={[
                  { value: "", label: "Zubuchung (Aufpreis)" },
                  { value: "mindestumsatz", label: "Mindestumsatz inkl. Menü" },
                ]}
              />
            </label>
          )}
          {verbindbar && onVerbinden && (verbindbar.length > 0 || item.addonGruppe) && (
            <label className="flex items-center gap-1.5 font-body-sm text-[12px] text-on-surface-variant">
              Mit „und“ verbunden
              <Select
                className="!w-auto !max-w-[14rem] !py-0.5 !text-[12px]"
                ariaLabel="Mit einer anderen Zubuchung verbunden (ein gemeinsamer Schalter)"
                value={item.addonGruppe ? (verbindbar.find((v) => v.gruppe === item.addonGruppe)?.id ?? "__bestehend") : ""}
                onValueChange={(v) => onVerbinden(v === "" || v === "__bestehend" ? null : v)}
                options={[
                  { value: "", label: "nicht verbunden" },
                  ...(item.addonGruppe && !verbindbar.some((v) => v.gruppe === item.addonGruppe) ? [{ value: "__bestehend", label: "verbunden (andere Position fehlt)" }] : []),
                  ...verbindbar.map((v) => ({ value: v.id, label: v.name || "(ohne Namen)" })),
                ]}
              />
            </label>
          )}
        </div>
      )}
      <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="font-body-sm text-[11px] text-on-surface-variant/60">{lineTotalCaption(item, gaeste)}</p>
        {/* LMIV: Allergene/Diät je Position — bei Speisen/Getränken IMMER sichtbar, auch der
            ungepflegte Zustand („–"), damit fehlende Pflichtangaben nie stumm untergehen. */}
        {(item.section === "menu" || item.section === "drinks") && (
          <span className="inline-flex items-center gap-1">
            <span className="font-label-md text-[10px] uppercase tracking-wider text-on-surface-variant/50">Allergene</span>
            <AllergenBadges allergens={lmiv ? lmiv.allergens : null} checked={lmiv?.allergensCheckedAt != null} dietary={lmiv?.dietary} />
          </span>
        )}
      </div>
      {/* B-4 (Entwurf 31b): der Abschluss der EINZELN geoeffneten Zeile. „Fertig" klappt nur zu —
          gespeichert wurde beim Tippen. „Abbrechen" setzt auf den Stand beim Oeffnen zurueck und
          klappt dann zu; auch das laeuft ueber den normalen Autosave, damit im Angebot steht, was
          auf dem Bildschirm steht. */}
      {/* Mockup v3: ohne Zeiger (Touch) gibt es die Werkzeuge der Lesezeile nicht — was dort
          stand, ist deshalb auch hier im Formular erreichbar. */}
      {paketKopf && item.priceMode === "per_person" && guests > 1 && (
        <p className="mt-2 flex items-center gap-1.5 text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim" data-no-dnd="true">
          für
          <TeilgruppeEingabe item={item} guests={guests} onPatch={onPatch} />
          von {guests} Gästen
        </p>
      )}
      {paketKopf && <MengeAlsGaesteHinweis item={item} guests={guests} onPatch={onPatch} />}
      {(onGruppeVerlassen && item.choiceGroupId != null) || onNeuerGangAbHier ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {onGruppeVerlassen && item.choiceGroupId != null && (
            <Button type="button" variant="ghost" data-no-dnd="true" className="!px-2 !py-1 text-beleg-mikro text-on-surface-variant dark:text-secondary-fixed-dim" onClick={onGruppeVerlassen}>
              <Icon name="link_off" className="text-[16px]" /> Aus Wahlgruppe lösen
            </Button>
          )}
          {onNeuerGangAbHier && (
            <Button type="button" variant="ghost" data-no-dnd="true" className="!px-2 !py-1 text-beleg-mikro text-on-surface-variant dark:text-secondary-fixed-dim" onClick={onNeuerGangAbHier}>
              <Icon name="call_split" className="text-[16px]" /> Neuer Gang ab hier
            </Button>
          )}
        </div>
      ) : null}
      {einzeln && (
        <div className="mt-2 flex items-center justify-end gap-1.5">
          <Button type="button" variant="ghost" data-no-dnd="true" className="!px-2 !py-1 text-beleg-mikro text-on-surface-variant" onClick={onAbbrechen}>
            Abbrechen
          </Button>
          <Button type="button" variant="ghost" data-no-dnd="true" className="!px-2 !py-1 text-beleg-mikro text-primary" onClick={onFertig}>
            <Icon name="check" className="text-[16px]" /> Fertig
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}
